import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from "../../../apiClient";
import { ProductDto, UpdateProductDto } from "../../../models/product/dto/product.dto";
import { GET_PRODUCT_QUERY_KEY } from "./useGetProduct";
import { GET_PRODUCTS_QUERY_KEY } from "./useListProducts";

export type UpdateProductWithImageInput = {
    productData: Omit<UpdateProductDto, 'imageUrl'>;
    imageFile?: File;
};

export const useUpdateProduct = (id: string, onSuccess?: () => void) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['updateProduct', id],
        mutationFn: async (input: UpdateProductDto | UpdateProductWithImageInput) => {
            // Check if it's the new format with image file
            if ('productData' in input) {
                const formData = new FormData();

                // Append all product data fields
                Object.entries(input.productData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        if (Array.isArray(value)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, value.toString());
                        }
                    }
                });

                // Append image file if provided
                if (input.imageFile) {
                    formData.append('image', input.imageFile);
                }

                return apiClient.fetchApiWithFormData<ProductDto>({
                    endpoint: `products/${id}/with-image`,
                    method: 'PATCH',
                    formData
                });
            } else {
                // Legacy format - regular JSON request
                return apiClient.fetchApi<ProductDto, UpdateProductDto>({
                    method: 'PATCH',
                    endpoint: `products/${id}`,
                    body: input
                });
            }
        },
        onSuccess: (updatedProduct) => {
            // Update the specific product cache
            queryClient.setQueryData<ProductDto>(GET_PRODUCT_QUERY_KEY(id), updatedProduct);

            // Update the products list cache
            queryClient.setQueryData<ProductDto[]>(
                GET_PRODUCTS_QUERY_KEY(updatedProduct.locationId),
                (prevList = []) => {
                    return prevList.map(product =>
                        product.id === id ? updatedProduct : product
                    );
                }
            );

            // Also update the general products list (without location filter)
            queryClient.setQueryData<ProductDto[]>(
                GET_PRODUCTS_QUERY_KEY(),
                (prevList = []) => {
                    return prevList.map(product =>
                        product.id === id ? updatedProduct : product
                    );
                }
            );

            onSuccess?.();
        }
    });
};
